import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { object } from 'yup';

import { enqueueSnackbar, fieldValidations, FormProvider, maskPhoneNumber, RHFCode } from '@zeta-core';
import { getErrorMessage, UserServiceAPIs } from '@zeta-services';
import { IGenericErrorResponse, IUserLogin2FASendRequest, IUserLogin2FAVerifyRequest } from '@zeta-types';

import { CommonsConfig } from '../../../../lib/contexts';
import { PortalNameType } from '../../../../lib/types';

export const LoginVerificationPageCommon = () => {
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [remainingTime, setRemainingTime] = useState(60);

  const { state } = useLocation();
  const navigate = useNavigate();
  const { portal } = CommonsConfig();

  const emailVerificationSchema = object().shape({
    code: fieldValidations.verificationCode,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsResendDisabled(false);
      setRemainingTime(0);
    }, 60000);

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev > 1) {
          return prev - 1;
        } else {
          clearInterval(interval);
          return 0;
        }
      });
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!state && !state.loginData && !state.loginData.token) {
      navigate('/auth');
    }
  }, []);

  const verification2FaSendApi = useMutation({
    mutationFn: (payload: IUserLogin2FASendRequest) => {
      return UserServiceAPIs().userLogin2FAVerificationSend(payload);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
      if (errorCode === 'U010012') navigate('/auth');
    },
  });

  const verification2FaVerifyApi = useMutation({
    mutationFn: (payload: IUserLogin2FAVerifyRequest) => {
      return UserServiceAPIs().userLogin2FAVerificationVerify(payload);
    },

    onSuccess: ({ data: response }) => {
      sessionStorage.setItem('accessToken', response?.accessToken);
      sessionStorage.setItem('refreshToken', response?.refreshToken);

      const userStatus = response?.userStatus;
      if (portal === PortalNameType.ADVISOR && (userStatus === 'KYC_PENDING' || userStatus === 'KYC_FAILED')) {
        sessionStorage.setItem('userStatus', userStatus);
        navigate('/kyc-status');
      } else {
        navigate('/', { state: { email: state.loginData.email } });
      }
    },

    onError: (error) => {
      if (error?.response?.data.error.errorCode === 'U010052') {
        methods.setError('code', {
          type: 'manual',
          message: 'Invalid verification code.',
        });
      } else {
        // U010016 error code
        methods.setError('code', {
          type: 'manual',
          message: 'Verification code expired.',
        });
      }
    },
  });

  const verification2FaSendApiFn = () =>
    verification2FaSendApi.mutateAsync({
      ...state?.loginData,
      isRequestedForEmail: false,
    });

  const methods = useForm({
    defaultValues: { code: '' },
    resolver: yupResolver(emailVerificationSchema),
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const onSubmit = async (data: { code: string }) => {
    const payload = {
      ...state.loginData,
      code: data.code,
      isVerifiedByMobile: true,
    };

    verification2FaVerifyApi.mutateAsync(payload);
  };

  const handleResendClick = () => {
    verification2FaSendApiFn();
    setIsResendDisabled(true);
    setRemainingTime(60);
    setTimeout(() => {
      setIsResendDisabled(false);
      setRemainingTime(0);
    }, 60000);

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev > 1) {
          return prev - 1;
        } else {
          clearInterval(interval);
          return 0;
        }
      });
    }, 1000);
  };

  return (
    <Stack>
      <Typography variant="h3" id="verification-title">
        Verification
      </Typography>
      <Typography variant="body1" id="verification-sub-title">
        Please enter the 6-digit verification code we sent to{' '}
        {state.loginData.mobile ||
          maskPhoneNumber(
            state.loginData.mobileWCountryCode.slice(0, 3),
            state.loginData.mobileWCountryCode.slice(3, state.loginData.mobileWCountryCode.length)
          )}
        . This code expires soon.
      </Typography>
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack marginTop={5}>
          <RHFCode sx={{ minWidth: 300 }} name="code" id="otp-box" />
        </Stack>
        <Typography marginTop={5} variant="body1" id="expire-code-text">
          This code expires within 60s.
        </Typography>
        <Box marginTop={5} display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" id="sub-text">
            Haven’t received?
          </Typography>
          <LoadingButton onClick={handleResendClick} variant="text" id="resend-otp-link" color="primary" disabled={isResendDisabled}>
            {isResendDisabled ? `Resend Code (${remainingTime}s)` : 'Resend Code'}
          </LoadingButton>
        </Box>
        <Stack marginTop={5}>
          <Button type="submit" variant="contained" color="primary" id="verify-button" disabled={verification2FaVerifyApi.isPending}>
            Verify
          </Button>
        </Stack>
        <Box marginTop={5} display="flex" alignItems="center">
          <Typography variant="body1" id="account-creation-text">
            Don’t have an account?{' '}
          </Typography>
          <LoadingButton variant="text" color="primary" id="register-account" onClick={() => navigate('/signup/accounts/terms')}>
            Register account
          </LoadingButton>
        </Box>
      </FormProvider>
    </Stack>
  );
};

export default LoginVerificationPageCommon;
